// The filter drawer's "Restid till jobbet" section (both views): the workplace, how you travel and an
// optional time limit. Changes are a draft until the drawer's search button applies them (commute.js); areas outside the limit are treated like
// areas outside the area-score filter. Hidden while neither routing engine answers; with one down its modes
// are greyed out in the menu, and a chosen mode that's down says so.
import React, { useEffect, useRef, useState } from 'react';
import { track } from '../analytics/events.js';
import { markIntroSeen, requestIntroTip } from './IntroTips.js';
import {
    COMMUTE_MAX_STEPS,
    COMMUTE_MODE_ICON_PATHS,
    COMMUTE_MODES,
    commuteModeLabel,
    getCommuteDraft,
    searchWorkplace,
    setCommuteDraft,
    useCommuteDraft,
    modeWorks,
    useCommuteReach,
    useTravelEngines,
} from './commute.js';
import './commute.css';

const MODE_ICONS = COMMUTE_MODE_ICON_PATHS;

export const CommuteModeIcon = ({ mode, size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={MODE_ICONS[mode] || MODE_ICONS.transit} />
    </svg>
);

const CommuteFilter = ({ languageState, introTip = 'open' }) => {
    const swedish = languageState?.swedish !== false;
    const engines = useTravelEngines();
    const available = engines ? (engines.transit || engines.road) : null;
    const commute = useCommuteDraft();
    const { reach, loading } = useCommuteReach(commute);
    const [editing, setEditing] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [modeOpen, setModeOpen] = useState(false);
    const timerRef = useRef(null);
    const modeRef = useRef(null);
    const sectionRef = useRef(null);
    const hasPlace = Boolean(commute.place);

    // The shared "Restid till jobbet" intro tip (SHARED_TIPS in introScenes.js), the first time the filters open with
    // no workplace set: as this section mounts in a drawer, sheet or panel just opened, or in listvy's filter column,
    // which is always open (`introTip` 'interact'), at the first touch of its filters. Anyone with a workplace has
    // found it.
    useEffect(() => {
        if (available !== true) return undefined;
        if (hasPlace) {
            markIntroSeen('commute');
            return undefined;
        }
        if (introTip !== 'interact') {
            requestIntroTip('commute');
            return undefined;
        }
        const scope = sectionRef.current?.closest('.drawer-panel') || sectionRef.current?.parentElement;
        if (!scope) return undefined;
        const onTouch = () => requestIntroTip('commute');
        scope.addEventListener('pointerdown', onTouch);
        scope.addEventListener('focusin', onTouch);
        return () => {
            scope.removeEventListener('pointerdown', onTouch);
            scope.removeEventListener('focusin', onTouch);
        };
    }, [available, hasPlace, introTip]);

    // Close the travel-mode dropdown on an outside click or Escape.
    useEffect(() => {
        if (!modeOpen) return undefined;
        const onDown = (event) => {
            if (modeRef.current && !modeRef.current.contains(event.target)) setModeOpen(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') setModeOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [modeOpen]);

    useEffect(() => {
        clearTimeout(timerRef.current);
        const text = query.trim();
        if (text.length < 3) {
            setResults([]);
            setSearching(false);
            return undefined;
        }
        setSearching(true);
        timerRef.current = setTimeout(() => {
            searchWorkplace(text)
                .then((found) => setResults(found))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 350);
        return () => clearTimeout(timerRef.current);
    }, [query]);

    if (available !== true) return null;

    const showSearch = editing || !commute.place;
    const stepIndex = Math.max(0, COMMUTE_MAX_STEPS.indexOf(commute.maxMinutes));
    const limitText = commute.maxMinutes
        ? (swedish ? `Högst ${commute.maxMinutes} min` : `At most ${commute.maxMinutes} min`)
        : (swedish ? 'Ingen gräns' : 'No limit');

    const pickPlace = (place) => {
        setCommuteDraft({ place });
        setEditing(false);
        setQuery('');
        setResults([]);
        track('commute.place', { set: true });
    };

    // The heading's right end: the limit, and with a limit how many areas it reaches.
    let reachText = '';
    if (commute.place && commute.maxMinutes) {
        if (loading) reachText = swedish ? 'räknar…' : 'counting…';
        else if (reach) reachText = swedish ? `${reach.size} områden` : `${reach.size} areas`;
    }

    // The travel modes, as a dropdown button on the right of the workplace field: the button shows the chosen
    // mode's icon, the menu lists all four.
    const modeDropdown = (
        <div className="commute-mode-dd" ref={modeRef}>
            <button
                type="button"
                className="commute-mode-dd-btn"
                aria-haspopup="listbox"
                aria-expanded={modeOpen}
                aria-label={`${swedish ? 'Färdsätt' : 'Travel mode'}: ${commuteModeLabel(commute.mode, swedish)}`}
                title={commuteModeLabel(commute.mode, swedish)}
                onClick={() => setModeOpen((open) => !open)}
            >
                <span className="commute-mode-dd-icon"><CommuteModeIcon mode={commute.mode} /></span>
                <svg className="commute-mode-chev" width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {modeOpen && (
                <ul className="commute-mode-menu" role="listbox" aria-label={swedish ? 'Färdsätt' : 'Travel mode'}>
                    {COMMUTE_MODES.map((mode) => {
                        const works = modeWorks(engines, mode);
                        return (
                            <li key={mode}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={commute.mode === mode}
                                    aria-disabled={!works}
                                    disabled={!works}
                                    className={`commute-mode-opt${commute.mode === mode ? ' is-active' : ''}${works ? '' : ' is-down'}`}
                                    onClick={() => {
                                        setCommuteDraft({ mode });
                                        track('commute.mode', { mode });
                                        setModeOpen(false);
                                    }}
                                >
                                    <CommuteModeIcon mode={mode} />
                                    <span>{commuteModeLabel(mode, swedish)}</span>
                                    {!works && <small>{swedish ? 'Inte just nu' : 'Not right now'}</small>}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );

    return (
        <div ref={sectionRef} className="drawer-section commute-filter">
            <div className="drawer-section-header commute-head">
                <div className="drawer-section-title">{swedish ? 'Restid' : 'Travel time to work'}</div>
                {commute.place && (
                    <span
                        className="commute-head-value"
                        aria-live="polite"
                        title={commute.maxMinutes ? (swedish ? 'Områden längre bort tonas ner' : 'Areas further away are greyed out') : undefined}
                    >
                        <b>{limitText}</b>
                        {reachText ? ` · ${reachText}` : ''}
                    </span>
                )}
            </div>

            {showSearch ? (
                <div className="commute-search">
                    <label className="commute-label" htmlFor="commute-place-input">{swedish ? 'Resmål' : 'Destination'}</label>
                    <div className="commute-field-row">
                        <input
                            id="commute-place-input"
                            className="commute-input"
                            type="text"
                            autoComplete="off"
                            placeholder={swedish ? 'Adress eller plats' : 'Address or place'}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                        {modeDropdown}
                    </div>
                    {(results.length > 0 || searching) && (
                        <ul className="commute-results" role="listbox" aria-label={swedish ? 'Förslag' : 'Suggestions'}>
                            {searching && !results.length && <li className="commute-result is-muted">{swedish ? 'Söker…' : 'Searching…'}</li>}
                            {results.map((result) => (
                                <li key={`${result.label}-${result.lat}`}>
                                    <button type="button" className="commute-result" onClick={() => pickPlace(result)}>{result.label}</button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {commute.place && (
                        <button type="button" className="commute-link" onClick={() => setEditing(false)}>
                            {swedish ? 'Avbryt' : 'Cancel'}
                        </button>
                    )}
                </div>
            ) : (
                <div className="commute-field-row">
                    <div className="commute-place">
                        <span className="commute-place-pin" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C7.6 2 4 5.4 4 9.7 4 15 12 22 12 22s8-7 8-12.3C20 5.4 16.4 2 12 2zm0 10.5a2.8 2.8 0 1 1 0-5.6 2.8 2.8 0 0 1 0 5.6z" /></svg>
                        </span>
                        <span className="commute-place-label">{commute.place.label}</span>
                        <button type="button" className="commute-link" onClick={() => setEditing(true)}>{swedish ? 'Ändra' : 'Change'}</button>
                        <button
                            type="button"
                            className="commute-link"
                            onClick={() => {
                                setCommuteDraft({ place: null, maxMinutes: null });
                                track('commute.place', { set: false });
                            }}
                        >
                            {swedish ? 'Ta bort' : 'Remove'}
                        </button>
                    </div>
                    {modeDropdown}
                </div>
            )}

            <div className="commute-limit">
                <input
                    id="commute-limit-input"
                    aria-label={swedish ? 'Högsta restid' : 'Longest travel time'}
                    aria-valuetext={limitText}
                    type="range"
                    min="0"
                    max={COMMUTE_MAX_STEPS.length - 1}
                    step="1"
                    value={stepIndex}
                    disabled={!commute.place}
                    onChange={(event) => setCommuteDraft({ maxMinutes: COMMUTE_MAX_STEPS[Number(event.target.value)] })}
                    onPointerUp={() => track('commute.limit', { minutes: getCommuteDraft().maxMinutes ?? 'none' })}
                    onKeyUp={() => track('commute.limit', { minutes: getCommuteDraft().maxMinutes ?? 'none' })}
                />
            </div>
            {commute.place && engines && !modeWorks(engines, commute.mode) && (
                <p className="commute-status is-down">
                    {swedish
                        ? `${commuteModeLabel(commute.mode, swedish)} går inte att räkna ut just nu.`
                        : `${commuteModeLabel(commute.mode, swedish)} isn't available right now.`}
                </p>
            )}
            {!commute.place && (
                <p className="commute-status">
                    {swedish
                        ? 'Ange ett resmål för att se restiden dit från varje bostad, fram kl. 08:30 en vardag.'
                        : 'Add a destination to see the travel time there from every home, arriving 08:30 on a weekday.'}
                </p>
            )}
        </div>
    );
};

export default CommuteFilter;
